// "Is this uuid a SESSION?" - the only question this answers.
//
// A uuid in prose may be an MCP server or an event, so one becomes clickable
// only on EXACT match against a session-typed column of a result this chat ran.
// No prefix matching: a truncated id is text, not a session id.

import { getScopeDimForColumn } from "./scopeColumns.js";

// Hitting either just means fewer links, which degrades to plain text.
export const MAX_INDEXED_IDS = 20000;
export const MAX_SCANNED_ROWS = 5000;

const UUID_EXACT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function createSessionIdIndex() {
    // lowercased uuid -> the API's spelling, so navigation ignores prose casing.
    const byLower = new Map();
    let version = 0;

    const add = (value) => {
        if (typeof value !== "string") return;
        const lower = value.toLowerCase();
        if (!UUID_EXACT.test(lower)) return;
        if (byLower.has(lower)) return;
        if (byLower.size >= MAX_INDEXED_IDS) return;
        byLower.set(lower, value);
        version += 1;
    };

    return {
        // Dict rows (the tool-result store's shape). Keys classified once, so a
        // result with no session column costs one pass over them.
        addDictRows(rows) {
            if (!Array.isArray(rows) || !rows.length) return;
            const first = rows[0];
            if (!first || typeof first !== "object") return;
            const keys = Object.keys(first).filter(
                (k) => getScopeDimForColumn(k) === "sessions",
            );
            if (!keys.length) return;
            const limit = Math.min(rows.length, MAX_SCANNED_ROWS);
            for (let i = 0; i < limit; i++) {
                const row = rows[i];
                if (!row) continue;
                for (const k of keys) add(row[k]);
            }
        },

        // Wire shape (the transcript's `details`). Not rowsToObjects: that
        // allocates a dict per row for the one or two cells wanted.
        addWireRows(columns, rows) {
            if (!Array.isArray(columns) || !Array.isArray(rows)) return;
            const idxs = [];
            for (let i = 0; i < columns.length; i++) {
                if (getScopeDimForColumn(columns[i]) === "sessions") idxs.push(i);
            }
            if (!idxs.length) return;
            const limit = Math.min(rows.length, MAX_SCANNED_ROWS);
            for (let i = 0; i < limit; i++) {
                const values = rows[i]?.values;
                if (!Array.isArray(values)) continue;
                for (const j of idxs) add(values[j]);
            }
        },

        resolve(id) {
            if (typeof id !== "string") return null;
            return byLower.get(id.toLowerCase()) || null;
        },

        get size() { return byLower.size; },
        // Bumps only on growth, so a matcher memoised on it stays stable.
        get version() { return version; },
    };
}

// Both sources are needed because they fail in opposite directions: store
// entries are evicted after compaction and past the row budget, while the
// transcript's `details` is clipped at 512 KB but survives persistence.
//
// `seen` short-circuits by object identity, which holds because `details` is
// assigned once and `entry.rows` is replaced rather than mutated.
export function ingestChatSources(index, { forEachResult, namespaceKey, items, seen }) {
    if (typeof forEachResult === "function" && namespaceKey) {
        forEachResult(namespaceKey, (rows) => {
            if (!rows || seen?.has(rows)) return;
            seen?.add(rows);
            index.addDictRows(rows);
        });
    }
    for (const item of items || []) {
        if (!item) continue;
        const children = item.kind === "turn" ? item.children : null;
        for (const node of children || []) {
            const details = node?.kind === "tool" ? node.details : null;
            if (!details || typeof details !== "object") continue;
            if (seen?.has(details)) continue;
            seen?.add(details);
            // analytics_query is wire-shaped; expand_preview is already dicts.
            if (Array.isArray(details.columns)) {
                index.addWireRows(details.columns, details.rows);
            } else if (Array.isArray(details.rows)) {
                index.addDictRows(details.rows);
            }
        }
    }
    return index.version;
}
